import type {
  AppState,
  GymDashboardData,
  TrainerDashboardData,
} from './appInitialState';
import type { Trainer } from '../types';

export const getSelectedGymId = (state: AppState): string =>
  state.selectedGymId ?? '';

export const withGymDashboard = (
  state: AppState,
  gymId: string,
  updater: (dashboard: GymDashboardData) => GymDashboardData,
): AppState => {
  const currentDashboard = state.gymDataMap[gymId];
  if (!currentDashboard) {
    return state;
  }
  const nextDashboard = updater(currentDashboard);
  if (nextDashboard === currentDashboard) {
    return state;
  }
  return {
    ...state,
    gymDataMap: {
      ...state.gymDataMap,
      [gymId]: nextDashboard,
    },
  };
};

export const withTrainerDashboard = (
  state: AppState,
  gymId: string,
  trainerId: string,
  updater: (dashboard: TrainerDashboardData) => TrainerDashboardData,
): AppState => {
  return withGymDashboard(state, gymId, (dashboard) => {
    const trainerDashboard = dashboard.trainers[trainerId];
    if (!trainerDashboard) {
      return dashboard;
    }
    const updatedTrainerDashboard = updater(trainerDashboard);
    if (updatedTrainerDashboard === trainerDashboard) {
      return dashboard;
    }
    return {
      ...dashboard,
      trainers: {
        ...dashboard.trainers,
        [trainerId]: updatedTrainerDashboard,
      },
    };
  });
};

export const upsertTrainerDashboard = (
  dashboard: GymDashboardData,
  trainer: Trainer,
): GymDashboardData => {
  const existing = dashboard.trainers[trainer.id];
  const mergedTrainerDashboard: TrainerDashboardData = {
    trainer,
    members: existing?.members ?? {},
    groups: existing?.groups ?? {},
    personalizedExercises: existing?.personalizedExercises ?? {},
    templates: existing?.templates ?? {},
    programs: existing?.programs ?? {},
  };

  return {
    ...dashboard,
    trainers: {
      ...dashboard.trainers,
      [trainer.id]: mergedTrainerDashboard,
    },
  };
};

